/* eslint-disable react/require-default-props */
import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import moment from 'moment';
import CardAgenda from 'components/atoms/card_agenda';
import { Container } from './styles';

type Event = {
  id: string;
  bidding_id: number;
  color: string;
  type: string;
  title: string;
  description: string;
  date: Date;
  companyId?: number;
  company?: string;
};

interface CalendarDashboardNewProps {
  eventsList: Array<{
    id: string;
    bidding_id: number;
    title: string;
    start: Date;
    className: string;
    description: string;
    type: string;
    companyId?: number;
    company?: string;
  }>;
  onActiveStartDateChange?: (activeStartDate: Date) => void;
}

const CalendarDashboardNew = (props: CalendarDashboardNewProps) => {
  const { eventsList, onActiveStartDateChange } = props;
  const [value, setValue] = useState<Date | null>(new Date());
  const [selectedEvents, setSelectedEvents] = useState<Event[]>([]);
  const [showAllEvents, setShowAllEvents] = useState(false);

  const getColorByType = (type: string) => {
    switch (type) {
      case 'bidding':
        return '#10A875';
      case 'task':
        return '#17a2b8';
      case 'progress':
        return '#ffc107';
      default:
        return '#ccc';
    }
  };

  useEffect(() => {
    const formattedDate = moment(value).format('YYYY-MM-DD');
    const initialEvents = eventsList.filter(
      (event) => moment(event.start).format('YYYY-MM-DD') === formattedDate,
    );
    setSelectedEvents(
      initialEvents.map((event) => ({
        id: event.id,
        bidding_id: event.bidding_id,
        color: getColorByType(event.type),
        type: event.type,
        title: event.title,
        description: event.description,
        date: event.start,
        companyId: event.companyId,
        company: event.company,
      })),
    );
  }, [eventsList, value]);

  const events = eventsList.reduce<{ [key: string]: Event[] }>((acc, event) => {
    const formattedDate = moment(event.start).format('YYYY-MM-DD');
    if (!acc[formattedDate]) {
      acc[formattedDate] = [];
    }
    acc[formattedDate].push({
      id: event.id,
      bidding_id: event.bidding_id,
      color: getColorByType(event.type),
      type: event.type,
      title: event.title,
      description: event.description,
      date: event.start,
      companyId: event.companyId,
      company: event.company,
    });
    return acc;
  }, {});

  const handleDateClick = (date: Date) => {
    setValue(date);
    const formattedDate = moment(date).format('YYYY-MM-DD');
    const eventsForDate = events[formattedDate] || [];
    setSelectedEvents(eventsForDate);
    setShowAllEvents(false);
  };

  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view === 'month') {
      const formattedDate = moment(date).format('YYYY-MM-DD');
      const dayEvents = events[formattedDate];

      if (dayEvents) {
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '2px',
              position: 'absolute',
              width: '100%',
              left: '0',
            }}
          >
            {dayEvents.map((event) => (
              <span
                key={event.id}
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: event.color,
                }}
              />
            ))}
          </div>
        );
      }
    }
    return null;
  };

  return (
    <Container>
      <div className="row">
        <div className="col-sm-6">
          <Calendar
            onClickDay={handleDateClick}
            onActiveStartDateChange={({ activeStartDate }) => {
              if (onActiveStartDateChange && activeStartDate)
                onActiveStartDateChange(activeStartDate);
            }}
            value={value}
            tileContent={tileContent}
            tileClassName={({ date, view }) => {
              if (view === 'month') {
                const day = date.getDay();
                if (day === 6 || day === 0) {
                  return 'weekend';
                }

                const today = new Date();
                return date.toDateString() === today.toDateString() ? 'highlight-today' : null;
              }
              return null;
            }}
          />
          <div className="legend mt-4">
            <div className="mr-3">
              <span className="legend-dot" style={{ backgroundColor: '#10A875' }} /> Licitação
            </div>
            <div className="mr-3">
              <span className="legend-dot" style={{ backgroundColor: '#17a2b8' }} /> Tarefa
            </div>
            <div>
              <span className="legend-dot" style={{ backgroundColor: '#ffc107' }} /> Andamento
            </div>
          </div>
        </div>
        <div className="col-sm-6">
          <h6 className="font-weight-bold mt-md-2 mt-4">
            Agenda do dia {moment(value).format('DD/MM')}
          </h6>
          <ul>
            {selectedEvents.length > 0 ? (
              <>
                {selectedEvents.slice(0, showAllEvents ? selectedEvents.length : 5).map((event) => (
                  <CardAgenda
                    key={event?.id}
                    date={moment(event?.date).format('HH:mm')}
                    description={event?.description}
                    title={event?.title}
                    color={event?.color}
                    type={event.type}
                    biddingId={event.bidding_id}
                    eventCompanyId={event.companyId}
                    company={event.company}
                  />
                ))}

                {selectedEvents.length > 5 && !showAllEvents && (
                  <a href="/minhas_licitacoes/management" className="mt-3 d-block text-center">
                    Ver mais
                  </a>
                )}
              </>
            ) : (
              <p>Nenhum evento para esta data.</p>
            )}
          </ul>
        </div>
      </div>
    </Container>
  );
};

export default CalendarDashboardNew;
